import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Facebook, Linkedin, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import GoogleMap from "@/components/GoogleMap";

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('contact_messages')
        .insert({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone || null,
          service: formData.service,
          budget: formData.budget || null,
          message: formData.message
        });

      if (error) throw error;

      toast({
        title: "Message envoyé!",
        description: "Nous vous contacterons bientôt."
      });

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        service: "",
        budget: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Helmet>
        <title>Contact - Agro-Réfrigération</title>
        <meta 
          name="description" 
          content="Contactez Agro-Réfrigération pour tous vos besoins en équipements laitiers. Service d'urgence 24/7 disponible." 
        />
      </Helmet>

      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {/* Left Column - Contact Info */}
          <div className="space-y-8">
            <div>
              <div className="w-16 h-1 bg-accent mb-6"></div>
              <p className="text-sm uppercase tracking-wider text-muted-foreground mb-4">
                NOUS SOMMES À L'ÉCOUTE!
              </p>
              <h1 className="text-3xl md:text-4xl font-bold mb-6">
                Laissez-nous vous accompagner dans l'optimisation de votre production laitière
              </h1>
            </div>

            <div className="space-y-4 text-muted-foreground">
              <p>
                L'aventure Agro commence par une conversation. Vos besoins sont uniques, et nos solutions le sont aussi. Exprimez vos défis, vos aspirations, et laissez-nous concevoir avec vous l'avenir de votre exploitation.
              </p>
              <p>
                Ce formulaire est le point de départ. Il est l'invitation à partager votre univers laitier, à esquisser ensemble une trajectoire d'efficacité et de prospérité. Agro est à votre écoute, prêt à transformer vos défis en opportunités.
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-muted-foreground">
                Préférez-vous discuter via Messenger?{" "}
                <a 
                  href="https://www.facebook.com/agrorefrigeration/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-accent hover:underline font-medium"
                >
                  Rejoignez-nous ici!
                </a>
              </p>

              <div className="flex gap-4">
                <a
                  href="https://www.facebook.com/agrorefrigeration/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-[#2c2c2c] text-white hover:bg-accent transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href="https://www.linkedin.com/company/agro-r%C3%A9frig%C3%A9ration-inc/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-[#2c2c2c] text-white hover:bg-accent transition-colors"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="mailto:admin@agroref.ca"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-[#2c2c2c] text-white hover:bg-accent transition-colors"
                  aria-label="Email"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">
                    Prénom <span className="text-accent">*</span>
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Ex: John"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">
                    Nom de famille <span className="text-accent">*</span>
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Ex: Smith"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    Adresse email: <span className="text-accent">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Ex: johnsmith@gmail.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone"># de téléphone (facultatif)</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Ex: 819 123 4567"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="service">
                    Quelle prestation vous intéresse? <span className="text-accent">*</span>
                  </Label>
                  <Input
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    placeholder="Ex: Assistance à la traite"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget">Quel est votre budget? (facultatif)</Label>
                  <Input
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="Ex: 5000$"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">
                  Message: <span className="text-accent">*</span>
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Donnez-nous des détails ici..."
                  rows={5}
                  required
                />
              </div>

              <div className="space-y-4">
                <Button 
                  type="submit" 
                  className="w-full bg-accent hover:bg-accent/90 text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Envoi en cours..." : "Envoyer le message"}
                </Button>
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> Les champs munis d'un * sont requis
                </p>
              </div>
            </form>
          </div>
        </div>

        {/* Contact Information Section with Map */}
        <div className="mt-16 bg-[#2c2c2c] text-white p-8 md:p-12 rounded-lg">
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Left Column - Call to Action */}
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-6">Appelez-nous:</h2>
                <a 
                  href="tel:8197529288" 
                  className="text-3xl md:text-4xl font-bold text-accent hover:text-accent/90 transition-colors inline-block mb-8"
                >
                  (819) 752-9288
                </a>
              </div>
              
              <div className="space-y-4 text-white/90">
                <p>
                  Opter pour un échange en direct peut souvent être l'approche la plus efficace. 
                  Si vous favorisez une conversation spontanée pour discuter de vos défis et 
                  ambitions, notre équipe est là, prête à vous écouter et à vous conseiller.
                </p>
                <p>
                  N'hésitez pas à saisir le téléphone et composer le <strong>(819) 752-9288</strong>. Un 
                  membre qualifié de l'équipe Agro sera enchanté d'échanger avec vous au 
                  sujet de vos besoins spécifiques en matière de réfrigération laitière, et de 
                  commencer à esquisser des solutions sur-mesure pour votre exploitation.
                </p>
              </div>
            </div>

            {/* Right Column - Google Map */}
            <div className="h-full min-h-[400px]">
              <GoogleMap />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
